import { getAccessToken, isTokenExpired, refreshAccessToken, logout } from './auth';
import { jsonRequestInit } from './utils/jsonRequest';

export class ApiError extends Error {
  status: number;
  data: unknown;
  constructor(message: string, status: number, data: unknown) {
    super(message);
    this.status = status;
    this.data = data;
  }
}

interface ApiOptions {
  method?: string;
  /**
   * JSON payload for the request body. jsonRequestInit owns its encoding
   * (serialization + Content-Type) on both the authenticated and public
   * paths. There is deliberately no raw headers/body escape hatch: when the
   * encoding rule was per-call-site, forgetting the header was harmless on
   * the authenticated path and silent breakage on the public one.
   */
  json?: unknown;
  public?: boolean;
}

/** Method defaults to POST when a body is present, GET otherwise. */
function toRequestInit(options?: ApiOptions): RequestInit {
  if (options?.json !== undefined) {
    return jsonRequestInit(options.method ?? 'POST', options.json);
  }
  return { method: options?.method };
}

async function fetchWithAuth(url: string, options: RequestInit): Promise<Response> {
  let token = getAccessToken();
  if (!token || isTokenExpired(token)) {
    token = await refreshAccessToken();
    if (!token) {
      logout();
      throw new Error('Session expired');
    }
  }

  const headers = new Headers(options.headers || {});
  headers.set('Authorization', `Bearer ${token}`);

  let response = await fetch(url, { ...options, headers });
  if (response.status === 401) {
    token = await refreshAccessToken();
    if (!token) {
      logout();
      throw new Error('Session expired');
    }
    headers.set('Authorization', `Bearer ${token}`);
    response = await fetch(url, { ...options, headers });
  }
  return response;
}

export async function apiText(url: string, options?: ApiOptions): Promise<string> {
  const init = toRequestInit(options);
  const response = options?.public
    ? await fetch(url, init)
    : await fetchWithAuth(url, init);

  const text = await response.text();

  if (!response.ok) {
    let detail: unknown = null;
    try {
      detail = (JSON.parse(text) as Record<string, unknown>).detail;
    } catch {
      detail = null;
    }
    const message = typeof detail === 'string' ? detail : `Request failed (${response.status})`;
    throw new ApiError(message, response.status, text);
  }

  return text;
}

export async function apiJson<T = unknown>(url: string, options?: ApiOptions): Promise<T> {
  const init = toRequestInit(options);
  const response = options?.public
    ? await fetch(url, init)
    : await fetchWithAuth(url, init);

  let data: unknown = null;
  let text: string | null = null;
  try {
    data = await response.json();
  } catch {
    try {
      text = await response.text();
    } catch {
      text = null;
    }
    data = null;
  }

  if (!response.ok) {
    const errorData = data as Record<string, unknown> | null;
    const detail = errorData?.detail;
    const message = typeof detail === 'string' ? detail
      : typeof detail === 'object' && detail !== null && 'message' in detail ? String((detail as Record<string, unknown>).message)
      : (errorData?.error ?? errorData?.message ?? text);
    throw new ApiError(String(message || `Request failed (${response.status})`), response.status, data ?? text);
  }

  return data as T;
}
